import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { isConditionComplete, operatorsFor, resolvePath, sourceEntity, valueKind } from "../definition";
import { labelOf, operatorLabel, pathLabel } from "../labels";
import type { Catalog, ConditionSpec, ReportSpec } from "../types";
import { ghostButton, input, primaryButton } from "./styles";
import ValueInput from "./ValueInput";

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  initial?: ConditionSpec | null;
  schoolYearId: string | null;
  onApply: (condition: ConditionSpec) => void;
  onCancel: () => void;
};

type Option = { path: string; label: string; group: string };

/** Three steps in one small panel: which data, how to compare, which value. */
export default function ConditionEditor({ catalog, definition, initial, schoolYearId, onApply, onCancel }: Props): ReactElement {
  const { t } = useTranslation();
  const [path, setPath] = useState(initial?.path ?? "");
  const [operator, setOperator] = useState(initial?.operator ?? "");
  const [value, setValue] = useState(initial?.value ?? null);

  const options = useMemo<Option[]>(() => {
    const root = sourceEntity(catalog, definition.source);
    if (!root) return [];
    const result: Option[] = [];
    root.fields.filter((field) => field.filterable)
      .forEach((field) => result.push({ path: field.key, label: labelOf(t, field), group: labelOf(t, root) }));
    root.relations.forEach((relation) => {
      const target = catalog.entities[relation.entity];
      if (!target) return;
      const group = labelOf(t, relation);
      if (relation.cardinality === "MANY") {
        result.push({
          path: relation.key,
          label: String(t("reports.builder.count_of", { defaultValue: "{{name}}: number", name: group })),
          group,
        });
      }
      target.fields.filter((field) => field.filterable).forEach((field) =>
        result.push({ path: `${relation.key}.${field.key}`, label: labelOf(t, field), group }));
      // One step further for relations with one value (a child's class → its school year).
      target.relations.filter((nested) => nested.cardinality === "ONE").forEach((nested) => {
        const nestedTarget = catalog.entities[nested.entity];
        nestedTarget?.fields.filter((field) => field.filterable).forEach((field) => result.push({
          path: `${relation.key}.${nested.key}.${field.key}`,
          label: labelOf(t, field),
          group: `${group} › ${labelOf(t, nested)}`,
        }));
      });
    });
    return result;
  }, [catalog, definition.source, t]);

  const groups = Array.from(new Set(options.map((option) => option.group)));
  const resolved = path ? resolvePath(catalog, definition.source, path) : null;
  const operators = operatorsFor(catalog, resolved);
  const condition: ConditionSpec = { path, operator, value: valueKind(operator) === "NONE" ? null : value };
  const complete = Boolean(resolved) && operators.includes(operator) && isConditionComplete(condition);

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (complete) onApply(condition);
      }}
    >
      <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
        {t("reports.builder.condition_field", { defaultValue: "Data" })}
        <select
          value={path}
          onChange={(event) => {
            setPath(event.target.value);
            const next = operatorsFor(catalog, resolvePath(catalog, definition.source, event.target.value));
            setOperator(next[0] ?? "");
            setValue(null);
          }}
          className={`${input} mt-1`}
        >
          <option value="">{t("reports.builder.choose", { defaultValue: "Choose…" })}</option>
          {groups.map((group) => (
            <optgroup key={group} label={group}>
              {options.filter((option) => option.group === group).map((option) => (
                <option key={option.path} value={option.path}>{option.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      {resolved && (
        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300">
          {t("reports.builder.condition_operator", { defaultValue: "Condition" })}
          <select
            value={operator}
            onChange={(event) => {
              const previousKind = valueKind(operator);
              setOperator(event.target.value);
              if (valueKind(event.target.value) !== previousKind) setValue(null);
            }}
            className={`${input} mt-1`}
          >
            {operators.map((item) => (
              <option key={item} value={item}>{operatorLabel(t, item)}</option>
            ))}
          </select>
        </label>
      )}

      {resolved && operator && (
        <ValueInput
          catalog={catalog}
          resolved={resolved}
          operator={operator}
          schoolYearId={schoolYearId}
          value={value}
          onChange={setValue}
        />
      )}

      {resolved?.many && resolved.field && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("reports.builder.many_condition_hint", {
            defaultValue: "Rows are shown when at least one of the {{name}} matches.",
            name: pathLabel(t, { ...resolved, field: null }, path).split(":")[0].toLowerCase(),
          })}
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className={ghostButton} onClick={onCancel}>
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
        <button type="submit" className={primaryButton} disabled={!complete}>
          {t("reports.builder.apply", { defaultValue: "Apply" })}
        </button>
      </div>
    </form>
  );
}
